import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { runtimeSlicePayload } from '@/utils/dashboardSliceFilters'
import apiClient from '@/api/client'
import { fetchWidgetAnswer } from '@/api/widgetBatch'
import { useWidgetBatching } from '@/contexts/WidgetBatchContext'
import { useShareContext } from '@/contexts/ShareTokenContext'
import { categorySeriesResult } from '@/utils/categorySeries'
import {
  DRAFT_KEY_SUFFIX,
  WIDGET_DRAFT_DEBOUNCE_MS,
  draftCacheOptions,
  useWidgetDraftMode,
} from './useWidgetDraftMode'

export { WIDGET_DRAFT_DEBOUNCE_MS }

// Stable empties, so a chart's memoized model and option survive re-renders
// while a field is absent (a fresh `[]` per call never compares equal).
const NONE = []
const NO_METADATA = {}

/** Cache namespace for saved widgets. */
export const WIDGET_DATA_KEY = 'widget-data'

/** Cache namespace for unsaved drafts, which must never collide with the above. */
export const WIDGET_DATA_DRAFT_KEY = `${WIDGET_DATA_KEY}${DRAFT_KEY_SUFFIX}`

const DEFAULT_DATE_RANGE = { type: 'relative', value: 'last_30_days' }
const DEFAULT_GROUPING = { dimension: 'entry_date', interval: 'day' }

/**
 * Widget types the backend's `dashboard_aggregate` treats as chart widgets
 * (H2/E4b): including `widget_type` in the request for one of these adds
 * `annotations`/`targets` overlays to the response. Mirrors the backend's
 * own `dashboards.chart_configuration.CHART_TYPES` - every other widget
 * type (number, pie, donut, text, report, section, ...) is unaffected and
 * must not send this field at all, so its cache key and response shape stay
 * byte-identical to before H2.
 */
const CHART_TYPES = new Set(['line_chart', 'area_chart', 'bar_chart'])

/**
 * Hook to fetch widget data from aggregation API using React Query.
 * Enables automatic cache invalidation and refetching for real-time updates.
 *
 * Supports dashboard-level filters that get merged with widget-level filters.
 * Widget filters take precedence over dashboard filters for the same key.
 *
 * Pass `{ draft: true }` to preview an unsaved widget (D-4). A draft is
 * debounced by `WIDGET_DRAFT_DEBOUNCE_MS` and cached under
 * `WIDGET_DATA_DRAFT_KEY`, so an edit the user abandons never becomes what the
 * dashboard shows for the saved widget.
 *
 * @param {Object} widget - Widget configuration object
 * @param {Object} dashboardFilters - Dashboard-level filters { campus_id, partition_filters }
 * @param {boolean|{enabled?: boolean, draft?: boolean}} [options=true] - Legacy
 *   boolean `enabled`, or the options object
 * @returns {Object} - { data, metadata, loading, error, refetch, draftWidget }
 */
export const useWidgetData = (widget, dashboardFilters = {}, options = true) => {
  const shareContext = useShareContext()
  const shareToken = typeof shareContext === 'string'
    ? shareContext
    : (shareContext?.token || null)
  const shareQueryString = (typeof shareContext === 'object' && shareContext?.queryString)
    ? shareContext.queryString
    : ''
  const shareParentHost = (typeof shareContext === 'object' && shareContext?.parentHost)
    ? shareContext.parentHost
    : ''

  const { isDraft, effectiveWidget, enabled } = useWidgetDraftMode(widget, options)
  const isBatched = useWidgetBatching() && !shareToken && !isDraft
  // A viewer's non-persisted interval/range choice (chart_options.viewer_controls).
  // Shared links get the saved scope only, so the override is never sent there.
  const viewerOverride = !shareToken && options && typeof options === 'object' ? options.viewerOverride || null : null

  // Merge dashboard and widget filters (widget wins)
  const mergedFilters = useMemo(() => {
    const widgetConfig = effectiveWidget?.config_json || {}
    return {
      campus_id: widgetConfig.campus_id || dashboardFilters.campus_id || null,
      ...runtimeSlicePayload(dashboardFilters),
      partition_filters: {
        ...(dashboardFilters.partition_filters || {}),
        ...(widgetConfig.partition_filters || {}),
      },
    }
  }, [effectiveWidget?.config_json, dashboardFilters])

  const metricIds = useMemo(
    () => effectiveWidget?.metrics?.map((metric) => metric.id) || [],
    [effectiveWidget]
  )

  // Built once and used as both the request body and the draft cache key, so
  // the preview can never show one config while having asked for another.
  const requestPayload = useMemo(() => {
    if (!effectiveWidget) return null

    const payload = {
      metric_ids: metricIds,
      date_range: effectiveWidget.config_json?.date_range || DEFAULT_DATE_RANGE,
      partition_filters: mergedFilters.partition_filters,
      ...(mergedFilters.runtime_partition_filters ? { runtime_partition_filters: mergedFilters.runtime_partition_filters } : {}),
      ...(mergedFilters.runtime_campus_ids ? { runtime_campus_ids: mergedFilters.runtime_campus_ids } : {}),
      grouping: effectiveWidget.config_json?.grouping || DEFAULT_GROUPING,
    }

    // Standalone metric trends reuse this chart hook with a synthetic cache
    // ID. Only persisted dashboard widgets can claim saved widget scope.
    if (!isDraft && effectiveWidget.is_persisted !== false && effectiveWidget.id && effectiveWidget.id !== 'widget-draft') {
      payload.widget_id = effectiveWidget.id
      const dashboardId = effectiveWidget.dashboard?.id || effectiveWidget.dashboard_id
      if (dashboardId) payload.dashboard_id = dashboardId
    }

    if (mergedFilters.campus_id) {
      payload.campus_id = mergedFilters.campus_id
    }
    if (effectiveWidget.config_json?.chart_comparisons?.length) {
      payload.comparisons = effectiveWidget.config_json.chart_comparisons
    }
    // H2: only line/area/bar widgets get annotation/target overlays - see
    // CHART_TYPES above. Sent only when applicable, not as an explicit
    // `undefined`, so every pre-H2 request body (number, pie, donut, ...)
    // stays byte-identical.
    if (CHART_TYPES.has(effectiveWidget.widget_type)) {
      payload.widget_type = effectiveWidget.widget_type
    }

    if (viewerOverride && CHART_TYPES.has(effectiveWidget.widget_type)) {
      payload.viewer_override = viewerOverride
    }

    return payload
  }, [effectiveWidget, metricIds, mergedFilters, isDraft, viewerOverride])

  const queryKey = isDraft
    ? [WIDGET_DATA_DRAFT_KEY, requestPayload, shareQueryString, shareParentHost]
    : [WIDGET_DATA_KEY, effectiveWidget?.id, mergedFilters, shareQueryString, shareParentHost, shareToken, requestPayload]

  const {
    data: queryData,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!effectiveWidget) return null

      if (metricIds.length === 0) {
        throw new Error('No metrics configured for this widget')
      }

      if (isBatched) return fetchWidgetAnswer('chart', requestPayload, { signal })

      // Call aggregation API (use shared endpoint if share token exists)
      const url = shareToken
        ? `/dashboards/shared/${shareToken}/aggregate/${shareQueryString}`
        : '/dashboards/aggregate/'
      const headers = {}
      if (shareParentHost) {
        headers['X-Graphit-Parent-Host'] = shareParentHost
      }
      const response = await apiClient.post(url, requestPayload, { headers, signal })
      return response.data
    },
    // A draft with nothing selected yet is a state the form is still in, not a
    // failure: stay quiet rather than throwing "No metrics configured" at
    // somebody who has not finished choosing.
    // A confidential-metric placeholder has no metrics; its data request would 404.
    enabled: enabled && !!effectiveWidget && !effectiveWidget.is_restricted && (!isDraft || metricIds.length > 0),
    ...draftCacheOptions(isDraft),
    refetchOnWindowFocus: false,
  })

  const presentedData = useMemo(() => categorySeriesResult(queryData, effectiveWidget?.config_json?.category_series, metricIds), [queryData, effectiveWidget?.config_json?.category_series, metricIds])

  return {
    data: presentedData?.data || NONE,
    metadata: presentedData?.metadata || NO_METADATA,
    comparisons: queryData?.comparisons || NONE,
    // H2/E4b overlays: only present when the request asked for them
    // (CHART_TYPES above); empty for every other widget type.
    annotations: queryData?.annotations || NONE,
    targets: queryData?.targets || NONE,
    loading: isLoading,
    error: queryError?.apiError?.message || queryError?.message || presentedData?.metadata?.category_series_error || null,
    refetch,
    // The settled draft this result describes. Rendering the widget from this
    // rather than from the live form keeps the chart and its data in step.
    draftWidget: effectiveWidget,
  }
}
